import { type Page, typedApi } from "@/api/typed-client";
import { useQuery } from "@tanstack/react-query";

export type JobStatus = "queued" | "running" | "done" | "failed" | "cancelled";

export interface JobOut {
  id: string;
  kind: string;
  status: JobStatus;
  progress?: number;
  progress_message?: string | null;
  target_kind?: string | null;
  target_id?: string | null;
  error_message?: string | null;
  error_code?: string | null;
  queued_at: string;
  started_at: string | null;
  finished_at: string | null;
  worker_id?: string | null;
  heartbeat_at?: string | null;
}

export type JobDetail = JobOut;

export function durationMs(j: JobOut): number | null {
  if (!j.started_at) return null;
  const end = j.finished_at ?? new Date().toISOString();
  const ms = new Date(end).getTime() - new Date(j.started_at).getTime();
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
}

export function useJobs(
  projectId: string | null,
  params: { page?: number; pageSize?: number } = {},
) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  return useQuery({
    enabled: !!projectId,
    queryKey: ["jobs", projectId, page, pageSize],
    queryFn: async () => {
      const { data, error } = await typedApi.GET("/v1/jobs", {
        params: { query: { project_id: projectId as string, page, page_size: pageSize } },
      });
      if (error) throw new Error(JSON.stringify(error));
      return data as Page<JobOut>;
    },
    placeholderData: (prev) => prev,
    refetchInterval: (q) =>
      q.state.data?.items.some((j) => j.status === "running" || j.status === "queued")
        ? 5000
        : false,
  });
}

export function useJob(id: string) {
  return useQuery({
    queryKey: ["jobs", id],
    queryFn: async () => {
      const { data, error } = await typedApi.GET("/v1/jobs/{job_id}", {
        params: { path: { job_id: id } },
      });
      if (error) throw new Error(JSON.stringify(error));
      return data as JobDetail;
    },
    refetchInterval: (q) =>
      q.state.data?.status === "running" || q.state.data?.status === "queued" ? 2000 : false,
  });
}
